import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  CreditCard,
  BookOpen,
  Activity,
  RefreshCw,
  AlertCircle,
  Store,
  Settings,
  UserCog,
  ShoppingBag,
  MessagesSquare,
  BarChart3,
  PieChart,
  Network,
  ArrowUpRight,
  GraduationCap,
  Video,
  Headphones,
} from 'lucide-react';
import {
  PageHeader,
  HorizontalBars,
  AreaChart,
  DonutChart,
  EmptyState,
  MiniSpark,
} from '../components/ui/UIComponents';
import { getUtilisateurs } from '../services/adminMembres';
import { getPaiements } from '../services/adminPaiement';
import { authService } from '../services/authService';
import { useI18n } from '../i18n/useI18n';

const CHART_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#f43f5e', '#0ea5e9', '#8b5cf6', '#64748b'];

function extractList(response, keys) {
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response?.[key])) return response[key];
  }
  return [];
}

function groupByDay(items, dateKeys) {
  const buckets = {};
  items.forEach((item) => {
    const raw = dateKeys.map((k) => item[k]).find(Boolean);
    if (!raw) return;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return;
    const key = d.toISOString().slice(0, 10);
    buckets[key] = (buckets[key] || 0) + 1;
  });
  return Object.keys(buckets)
    .sort()
    .map((day) => ({ day, value: buckets[day] }));
}

function ProgressRing({ value, max = 100, size = 56, color = '#4f46e5', label }) {
  const r = 18;
  const c = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 48 48" className="w-full h-full -rotate-90">
        <circle cx="24" cy="24" r={r} fill="none" stroke="#e2e8f0" strokeWidth="5" />
        <circle
          cx="24"
          cy="24"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[10px] font-bold text-slate-700">{label ?? `${pct}%`}</span>
      </div>
    </div>
  );
}

function MiniBars({ values = [], color = '#4f46e5' }) {
  const max = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-0.5 h-8">
      {values.map((v, i) => (
        <div
          key={i}
          className="w-1.5 rounded-sm opacity-90"
          style={{ height: `${Math.max(8, Math.round((v / max) * 100))}%`, background: color }}
        />
      ))}
    </div>
  );
}

function EmptyAnalytics({ title, message }) {
  return (
    <div className="relative min-h-[180px] rounded-2xl border border-dashed border-slate-200 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <div className="relative h-full min-h-[180px] flex flex-col items-center justify-center text-center p-6">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center mb-3">
          <BarChart3 size={18} />
        </div>
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        <p className="text-xs text-slate-500 mt-1 max-w-sm">{message}</p>
      </div>
    </div>
  );
}

function ComboActivityChart({ days, usersByDay, paysByDay }) {
  if (!days.length) return null;
  const max = Math.max(...days.map((d) => Math.max(usersByDay[d] || 0, paysByDay[d] || 0)), 1);
  const w = 640;
  const h = 220;
  const pad = 28;
  const step = days.length > 1 ? (w - pad * 2) / (days.length - 1) : 0;

  const userPoints = days.map((d, i) => {
    const x = pad + i * step;
    const y = h - pad - ((usersByDay[d] || 0) / max) * (h - pad * 2);
    return `${x},${y}`;
  });
  const payBars = days.map((d, i) => {
    const val = paysByDay[d] || 0;
    const barH = (val / max) * (h - pad * 2);
    const x = pad + i * step - 6;
    const y = h - pad - barH;
    return { x, y, barH, val, label: d };
  });
  const userPath = userPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p}`).join(' ');

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-56">
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = h - pad - t * (h - pad * 2);
          return <line key={t} x1={pad} x2={w - pad} y1={y} y2={y} stroke="#e2e8f0" strokeWidth="1" />;
        })}
        {payBars.map((b, i) => (
          <rect key={i} x={b.x} y={b.y} width="12" height={Math.max(b.barH, 0)} rx="3" fill="#10b981" opacity="0.35">
            <title>{`${b.label} · Paiements: ${b.val}`}</title>
          </rect>
        ))}
        <path d={userPath} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {userPoints.map((p, i) => {
          const [x, y] = p.split(',').map(Number);
          return (
            <circle key={i} cx={x} cy={y} r="3.5" fill="#4f46e5">
              <title>{`${days[i]} · Inscriptions: ${usersByDay[days[i]] || 0}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500 px-1">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-indigo-600 rounded" /> Inscriptions</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-emerald-500/40" /> Paiements</span>
        <span className="text-slate-400">Survolez les points / barres pour le détail</span>
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-slate-400 px-1 overflow-hidden">
        <span>{days[0]}</span>
        <span>{days[days.length - 1]}</span>
      </div>
    </div>
  );
}

function CoverageBar({ label, state }) {
  const map = {
    connecte: { width: '100%', color: 'bg-emerald-500', text: 'Connecté', tone: 'text-emerald-700' },
    partiel: { width: '55%', color: 'bg-amber-500', text: 'Partiellement connecté', tone: 'text-amber-700' },
    preparation: { width: '28%', color: 'bg-blue-500', text: 'Préparation', tone: 'text-blue-700' },
    non: { width: '12%', color: 'bg-slate-300', text: 'Non connecté', tone: 'text-slate-500' },
  };
  const cfg = map[state] || map.non;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium text-slate-700">{label}</span>
        <span className={`font-semibold ${cfg.tone}`}>{cfg.text}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${cfg.color}`} style={{ width: cfg.width }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const user = authService.getCurrentUser();
  const [users, setUsers] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [usersOk, setUsersOk] = useState(false);
  const [paiementsOk, setPaiementsOk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersResponse, paiementsResponse] = await Promise.allSettled([
        getUtilisateurs(),
        getPaiements(),
      ]);

      if (usersResponse.status === 'fulfilled') {
        setUsers(extractList(usersResponse.value, ['utilisateurs', 'data', 'users']));
        setUsersOk(true);
      } else {
        setUsersOk(false);
      }

      if (paiementsResponse.status === 'fulfilled') {
        setPaiements(extractList(paiementsResponse.value, ['paiements', 'data']));
        setPaiementsOk(true);
      } else {
        setPaiementsOk(false);
      }

      setSyncedAt(new Date());
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const volumeFCFA = useMemo(
    () => paiements.reduce((acc, curr) => acc + (Number(curr.montant || curr.amount || 0) || 0), 0),
    [paiements]
  );

  const avgMontant = useMemo(() => {
    if (!paiements.length) return 0;
    return volumeFCFA / paiements.length;
  }, [paiements, volumeFCFA]);

  const statusRows = useMemo(() => {
    const counts = {};
    users.forEach((u) => {
      const s = (u.statut || u.status || 'actif').toString();
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value], i) => ({
      label,
      value,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [users]);

  const paymentStatusRows = useMemo(() => {
    const counts = {};
    paiements.forEach((p) => {
      const s = (p.statut || p.status || 'inconnu').toString();
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value], i) => ({
      label,
      value,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [paiements]);

  const temporal = useMemo(() => {
    const userDays = groupByDay(users, ['created_at', 'date_creation', 'inscrit_le', 'createdAt']);
    const payDays = groupByDay(paiements, ['created_at', 'date', 'date_paiement', 'createdAt']);
    const daySet = new Set([...userDays.map((d) => d.day), ...payDays.map((d) => d.day)]);
    const days = Array.from(daySet).sort().slice(-14);
    const usersByDay = Object.fromEntries(userDays.map((d) => [d.day, d.value]));
    const paysByDay = Object.fromEntries(payDays.map((d) => [d.day, d.value]));
    const series = days.map((d) => (usersByDay[d] || 0) + (paysByDay[d] || 0));
    return { days, usersByDay, paysByDay, series };
  }, [users, paiements]);

  const actifsCount = useMemo(() => {
    return users.filter((u) => {
      const s = (u.statut || u.status || 'actif').toString().toLowerCase();
      return ['actif', 'active'].includes(s);
    }).length;
  }, [users]);

  const greetingName = user?.prenom || user?.nom_complet || 'Administrateur';

  const workerNodes = [
    { name: 'Worker Admin', state: 'Opérationnel', level: 'ok' },
    { name: 'Paiement', state: paiementsOk ? 'Données reçues' : 'À vérifier', level: paiementsOk ? 'ok' : 'warn' },
    { name: 'Membres', state: usersOk ? 'Données reçues' : 'À vérifier', level: usersOk ? 'ok' : 'warn' },
    { name: 'Contenu', state: 'Intégration', level: 'warn' },
    { name: 'Marketplace', state: 'Préparation', level: 'prep' },
    { name: 'Boutique', state: 'Préparation', level: 'prep' },
    { name: 'Communauté', state: 'Préparation', level: 'prep' },
  ];

  const levelStyle = {
    ok: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    warn: 'border-amber-200 bg-amber-50 text-amber-800',
    prep: 'border-blue-200 bg-blue-50 text-blue-800',
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={`Bonjour, ${greetingName}`}
        subtitle="Centre de pilotage analytique de l’écosystème DSI Academy."
        actions={
          <button onClick={fetchDashboardData} className="dsi-btn-secondary text-xs">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span>{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        {syncedAt && <span>• Mis à jour à {syncedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>}
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-center justify-between text-rose-700 text-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={fetchDashboardData} className="dsi-btn-secondary text-xs">{t('common.retry')}</button>
        </div>
      )}

      {/* KPI graphiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="dsi-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Membres</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{loading ? '…' : usersOk ? users.length.toLocaleString('fr-FR') : '—'}</p>
              <p className="text-xs text-slate-400 mt-1">{usersOk ? `${actifsCount} actifs observés` : 'Connexion en attente'}</p>
            </div>
            <ProgressRing value={actifsCount} max={Math.max(users.length, 1)} color="#4f46e5" label={users.length ? `${Math.round((actifsCount / users.length) * 100)}%` : '—'} />
          </div>
          <div className="mt-3">
            {statusRows.length > 0 ? (
              <MiniBars values={statusRows.map((r) => r.value)} color="#4f46e5" />
            ) : (
              <div className="h-8 rounded-lg bg-slate-50 border border-dashed border-slate-200" />
            )}
          </div>
        </div>

        <div className="dsi-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Transactions</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{loading ? '…' : paiementsOk ? paiements.length.toLocaleString('fr-FR') : '—'}</p>
              <p className="text-xs text-slate-400 mt-1">{paiementsOk ? 'Paiements reçus' : 'Connexion en attente'}</p>
            </div>
            <div className="p-2.5 rounded-xl border bg-emerald-50 text-emerald-600 border-emerald-100">
              <CreditCard size={18} />
            </div>
          </div>
          <div className="mt-3">
            {paymentStatusRows.length > 0 ? (
              <MiniBars values={paymentStatusRows.map((r) => r.value)} color="#10b981" />
            ) : temporal.series.length > 1 ? (
              <MiniSpark points={temporal.series} color="#10b981" />
            ) : (
              <div className="h-8 rounded-lg bg-slate-50 border border-dashed border-slate-200" />
            )}
          </div>
        </div>

        <div className="dsi-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Volume observé</p>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
                {loading ? '…' : paiementsOk ? `${Math.round(volumeFCFA).toLocaleString('fr-FR')} FCFA` : '—'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {paiementsOk && paiements.length ? `Moyenne ${Math.round(avgMontant).toLocaleString('fr-FR')} FCFA` : 'Agrégat calculé côté interface'}
              </p>
            </div>
            <div className="p-2.5 rounded-xl border bg-amber-50 text-amber-600 border-amber-100">
              <Activity size={18} />
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-500"
              style={{ width: paiementsOk && volumeFCFA > 0 ? '100%' : '8%' }}
            />
          </div>
        </div>

        <div className="dsi-card p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Contenus</p>
              <p className="text-3xl font-bold text-slate-400 mt-1">—</p>
              <p className="text-xs text-slate-400 mt-1">Worker Contenu en préparation</p>
            </div>
            <ProgressRing value={0} max={100} color="#8b5cf6" label="—" />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-1">
            {['Livres', 'Form.', 'Vidéos', 'Audios'].map((t) => (
              <div key={t} className="h-8 rounded-lg bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-[9px] text-slate-400">
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activité + répartition membres */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 dsi-card p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Évolution de l’activité</h3>
              <p className="text-xs text-slate-500 mt-0.5">Inscriptions et paiements regroupés uniquement lorsque des dates réelles existent.</p>
            </div>
            <PieChart size={16} className="text-slate-400" />
          </div>
          {temporal.days.length > 1 ? (
            <ComboActivityChart days={temporal.days} usersByDay={temporal.usersByDay} paysByDay={temporal.paysByDay} />
          ) : (
            <EmptyAnalytics
              title="Données analytiques indisponibles"
              message="Le graphique temporel sera automatiquement alimenté lorsque des dates exploitables seront présentes dans les listes reçues."
            />
          )}
        </div>

        <div className="dsi-card p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Répartition des membres</h3>
          <p className="text-xs text-slate-500 mb-4">Donut calculé depuis getUtilisateurs().</p>
          {statusRows.length ? (
            <DonutChart
              segments={statusRows}
              centerValue={users.length}
              centerLabel="Membres"
            />
          ) : (
            <EmptyAnalytics title="Pas encore de répartition" message="Aucun statut membre disponible pour construire le diagramme." />
          )}
        </div>
      </div>

      {/* Histogramme membres + analyse paiements */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="dsi-card p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Membres par statut</h3>
          <p className="text-xs text-slate-500 mb-4">Histogramme horizontal — même source que le donut.</p>
          {statusRows.length ? (
            <HorizontalBars
              rows={statusRows.map((r) => ({
                label: r.label,
                value: r.value,
                color: 'bg-indigo-500',
              }))}
            />
          ) : (
            <EmptyAnalytics title="Histogramme en attente" message="Les barres apparaîtront dès réception d’utilisateurs." />
          )}
        </div>

        <div className="dsi-card p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Répartition des paiements</h3>
          <p className="text-xs text-slate-500 mb-4">Statuts réellement présents dans getPaiements().</p>
          {paymentStatusRows.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <DonutChart segments={paymentStatusRows} centerValue={paiements.length} centerLabel="Tx" />
              <HorizontalBars
                rows={paymentStatusRows.map((r) => ({
                  label: r.label,
                  value: r.value,
                  color: 'bg-emerald-500',
                }))}
              />
            </div>
          ) : (
            <EmptyAnalytics
              title="Analyse paiements indisponible"
              message="Le graphique sera alimenté lorsque des paiements (et éventuellement des statuts) seront exposés."
            />
          )}
        </div>
      </div>

      {/* Volume financier */}
      <div className="dsi-card p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Volume financier observé</h3>
            <p className="text-xs text-slate-500 mt-0.5">Somme et répartition calculées uniquement sur les montants reçus.</p>
          </div>
          <span className="text-sm font-bold text-slate-900">
            {paiementsOk ? `${Math.round(volumeFCFA).toLocaleString('fr-FR')} FCFA` : '—'}
          </span>
        </div>
        {paiementsOk && paiements.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] text-slate-500 font-semibold uppercase">Transactions</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{paiements.length}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] text-slate-500 font-semibold uppercase">Volume total</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{Math.round(volumeFCFA).toLocaleString('fr-FR')}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] text-slate-500 font-semibold uppercase">Montant moyen</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{Math.round(avgMontant).toLocaleString('fr-FR')}</p>
            </div>
            {temporal.days.length > 1 && (
              <div className="md:col-span-3">
                <AreaChart series={temporal.days.map((d) => temporal.paysByDay[d] || 0)} />
              </div>
            )}
          </div>
        ) : (
          <EmptyAnalytics
            title="Données analytiques indisponibles"
            message="Le module volume sera alimenté dès que le Worker Admin exposera des paiements avec montants."
          />
        )}
      </div>

      {/* Univers Contenus — structure graphique préparée */}
      <div className="dsi-card p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Univers Contenus</h3>
            <p className="text-xs text-slate-500 mt-0.5">Structure analytique prête — en attente du Worker Contenu.</p>
          </div>
          <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-full">Préparation</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {[
            { title: 'Livres', icon: BookOpen, color: 'text-indigo-600 bg-indigo-50 border-indigo-100' },
            { title: 'Formations', icon: GraduationCap, color: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
            { title: 'Vidéos', icon: Video, color: 'text-amber-600 bg-amber-50 border-amber-100' },
            { title: 'Audios', icon: Headphones, color: 'text-violet-600 bg-violet-50 border-violet-100' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className={`p-2 rounded-xl border ${item.color}`}>
                    <Icon size={16} />
                  </div>
                  <ProgressRing value={0} max={100} color="#94a3b8" label="—" />
                </div>
                <p className="text-sm font-semibold text-slate-900 mt-3">{item.title}</p>
                <p className="text-2xl font-bold text-slate-300 mt-1">—</p>
                <p className="text-[11px] text-slate-400 mt-1">Données en attente du Worker Contenu</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Marketplace + Boutique + Communauté */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="dsi-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Marketplace DSI</h3>
            <Store size={16} className="text-slate-400" />
          </div>
          <EmptyAnalytics
            title="Intégration analytique en préparation"
            message="Vendeurs, listings, commandes et volume seront branchés dès exposition des données Marketplace."
          />
          <div className="grid grid-cols-2 gap-2 mt-3">
            {['Vendeurs', 'Listings', 'Commandes', 'Volume'].map((t) => (
              <div key={t} className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">{t}</p>
                <p className="text-lg font-bold text-slate-300">—</p>
              </div>
            ))}
          </div>
        </div>

        <div className="dsi-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Boutique DSI</h3>
            <ShoppingBag size={16} className="text-slate-400" />
          </div>
          <EmptyAnalytics
            title="Module distinct du Marketplace"
            message="Produits institutionnels, commandes et codes promo — aucune valeur fictive affichée."
          />
          <div className="grid grid-cols-2 gap-2 mt-3">
            {['Produits', 'Commandes', 'Codes promo', 'Activité'].map((t) => (
              <div key={t} className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">{t}</p>
                <p className="text-lg font-bold text-slate-300">—</p>
              </div>
            ))}
          </div>
        </div>

        <div className="dsi-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Communauté DSI</h3>
            <MessagesSquare size={16} className="text-slate-400" />
          </div>
          <EmptyAnalytics
            title="En attente de synchronisation"
            message="Espaces, conversations, messages et activité — Worker Communauté non connecté."
          />
          <div className="grid grid-cols-3 gap-2 mt-3">
            {['Espaces', 'Messages', 'Conversations'].map((t) => (
              <div key={t} className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-2 py-2 text-center">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">{t}</p>
                <p className="text-lg font-bold text-slate-300">—</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Architecture écosystème */}
      <div className="dsi-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">État de l’écosystème</h3>
            <p className="text-xs text-slate-500 mt-0.5">Le Worker Admin orchestre. Chaque Worker reste propriétaire de son domaine.</p>
          </div>
          <Network size={16} className="text-slate-400" />
        </div>

        <div className="flex flex-col items-center gap-3">
          <div className={`px-4 py-3 rounded-2xl border text-center min-w-[180px] ${levelStyle.ok}`}>
            <p className="text-xs font-bold">WORKER ADMIN</p>
            <p className="text-[11px] mt-0.5">Orchestrateur</p>
          </div>
          <div className="h-6 w-px bg-slate-300" />
          <div className="w-full max-w-3xl grid grid-cols-2 md:grid-cols-3 gap-3">
            {workerNodes.filter((n) => n.name !== 'Worker Admin').map((n) => (
              <div key={n.name} className={`rounded-2xl border px-3 py-3 text-center ${levelStyle[n.level]}`}>
                <p className="text-xs font-bold">{n.name}</p>
                <p className="text-[11px] mt-1">{n.state}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Couverture */}
      <div className="dsi-card p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Couverture de l’écosystème</h3>
        <p className="text-xs text-slate-500 mb-4">États qualitatifs — aucun pourcentage inventé.</p>
        <div className="space-y-3">
          <CoverageBar label="Administration" state="connecte" />
          <CoverageBar label="Paiement" state={paiementsOk ? 'connecte' : 'partiel'} />
          <CoverageBar label="Membres" state={usersOk ? 'connecte' : 'partiel'} />
          <CoverageBar label="Contenu" state="preparation" />
          <CoverageBar label="Marketplace" state="preparation" />
          <CoverageBar label="Boutique" state="preparation" />
          <CoverageBar label="Communauté" state="preparation" />
        </div>
      </div>

      {/* Actions rapides graphiques */}
      <div className="dsi-card p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Actions rapides</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {[
            { to: '/members', icon: Users, title: 'Membres', desc: 'Gérer les membres et consulter leur activité.', ready: usersOk },
            { to: '/finance', icon: CreditCard, title: 'Paiements', desc: 'Superviser transactions et volume observé.', ready: paiementsOk },
            { to: '/administrators', icon: UserCog, title: 'Administrateurs', desc: 'Comptes, niveaux et permissions RBAC.', ready: true },
            { to: '/library', icon: BookOpen, title: 'Contenus', desc: 'Catalogue livres, formations, vidéos, audios.', ready: false },
            { to: '/marketplace', icon: Store, title: 'Marketplace', desc: 'Économie entre membres — module en préparation.', ready: false },
            { to: '/security', icon: Settings, title: 'Configuration', desc: 'Paramètres et maintenance de la plateforme.', ready: true },
          ].map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.to + a.title}
                to={a.to}
                className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-indigo-200 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
                    <Icon size={18} />
                  </div>
                  <ArrowUpRight size={16} className="text-slate-300 group-hover:text-indigo-500 transition-colors" />
                </div>
                <p className="text-sm font-semibold text-slate-900 mt-3">{a.title}</p>
                <p className="text-xs text-slate-500 mt-1">{a.desc}</p>
                <p className={`text-[11px] font-semibold mt-2 ${a.ready ? 'text-emerald-600' : 'text-blue-600'}`}>
                  {a.ready ? 'Disponible' : 'Préparation'} · Ouvrir →
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
